import { Heart, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { PosterImage } from "./PosterImage";
import { formatRating } from "../lib/format";
import type { Stream } from "../lib/types";
import { useFavorite } from "../lib/useFavorite";

interface Props {
  stream: Stream;
  layout?: "landscape" | "portrait";
  href?: string;
}

export function StreamCard({ stream, layout = "portrait", href }: Props) {
  const { isFavorite: fav, toggle } = useFavorite(stream);
  const dest = href ?? defaultHref(stream);

  // Stream's type union is "live" | "vod" | "series" — catchup-only rows
  // are surfaced via /catchup/* with the underlying live id, so by the
  // time we reach a Stream here it's already one of the favourite-able
  // kinds. Per spec, catchup is folded into live via the `has_catchup`
  // flag rather than stored as its own kind.

  const toggleFav = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void toggle();
  };

  // Live channel logos are usually square — they look bad object-cover on a
  // 16:9 card. Use object-contain on a dark backdrop so the whole logo shows.
  // Movie/series posters are 2:3 already so cover is fine.
  const aspect = layout === "landscape" ? "aspect-video" : "aspect-[2/3]";
  const fit = layout === "landscape" ? "object-contain p-3" : "object-cover";
  const img = stream.logo || stream.cover;

  return (
    <Link
      to={dest}
      className="group block snap-start flex-shrink-0"
    >
      <div
        className={`relative ${aspect} rounded-lg overflow-hidden bg-canvas border border-muted/30 transition-fast group-hover:border-accent-gold/60`}
        style={{ containerType: "inline-size" }}
      >
        <PosterImage
          src={img}
          alt={stream.name}
          imgClass={`${fit} transition-standard group-hover:scale-[1.03]`}
          orientation={layout === "landscape" ? "row" : "stack"}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-canvas via-transparent opacity-60 group-hover:opacity-90 transition-fast" />
        {/* LIVE is a signal state, not an error. The design system reserves the
            accent for focus / primary / live, and reserves danger for things
            that have actually gone wrong. */}
        {stream.kind === "live" && (
          <div className="absolute top-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-pill bg-accent-gold text-canvas text-[9px] font-ui font-bold uppercase tracking-[0.06em]">
            <span className="w-1.5 h-1.5 rounded-full bg-canvas animate-live-pulse" />
            Live
          </div>
        )}
        <button
          type="button"
          onClick={toggleFav}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          className="absolute top-0.5 right-0.5 w-11 h-11 flex items-center justify-center rounded-full text-text-primary drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)] transition-fast z-10 sm:top-1.5 sm:right-1.5 sm:w-9 sm:h-9 sm:bg-canvas/70 sm:backdrop-blur-sm sm:drop-shadow-none sm:opacity-90 sm:hover:opacity-100 sm:hover:bg-canvas/90"
          aria-label={fav ? "Remove from favorites" : "Add to favorites"}
        >
          <Heart size={16} className={fav ? "fill-accent-gold text-accent-gold" : ""} />
        </button>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-fast">
          <div className="w-10 h-10 rounded-full bg-accent-gold/90 flex items-center justify-center">
            <Play size={18} className="text-canvas ml-0.5" fill="currentColor" />
          </div>
        </div>
      </div>
      <p className="mt-1.5 text-[12px] font-ui font-medium text-text-primary truncate">
        {stream.name}
      </p>
      {(() => {
        const rating = formatRating(stream.rating);
        const parts = [stream.year, rating].filter(Boolean);
        if (!parts.length) return null;
        return (
          <p className="text-[10px] font-mono uppercase tracking-[0.04em] text-text-secondary truncate">
            {parts.join(" · ")}
          </p>
        );
      })()}
    </Link>
  );
}

function defaultHref(s: Stream): string {
  if (s.kind === "series") return `/series/${encodeURIComponent(s.id)}`;
  return `/watch/${s.kind}/${encodeURIComponent(s.id)}`;
}
